import dynamic from 'next/dynamic';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import type { PageProps } from '@/core/page/interfaces/PageProps';
import { FlagsOverrideChecker } from '@/core/webTools/FlagsOverrideBanner/FlagsOverrideChecker';
import { isMobileAppUserAgentSelector } from '@/domains/core/client/client.selectors';
import {
  MetaTags,
  type MetaTagsProps,
} from '@/domains/core/page/components/MetaTags';
import { ProgressBar } from '@/domains/core/page/components/ProgressBar/ProgressBar';
import { usePageData } from '@/domains/core/page/hooks/usePageData';
import { BaseErrors } from '@/domains/core/shell/components/BaseErrors';
import { selectIsProcessingPayment } from '@/domains/customerFinanceOperations/Payment/selectors/pageStatus';
import { FunnelHeader } from '@/domains/customerManagement/components/FunnelHeader/FunnelHeader';

const Footer = dynamic(
  () => import('@/domains/retention/Footer/Footer').then((mod) => mod.Footer),
  {
    ssr: true,
    loading: () => {
      try {
        const placeholder = document.getElementById('lazyFooter');
        return (
          <div
            suppressHydrationWarning
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: placeholder?.innerHTML || '' }}
          />
        );
      } catch {
        return null;
      }
    },
  },
);

export interface FunnelPageProps extends PageProps, MetaTagsProps {
  headerBackText?: string;
  headerBackUrl?: string;
  headerOnBackAction?: () => void;
}

/**
 * Manages displaying the checkout funnel page layout and providing page
 * information to the datalayer.
 *
 * This component is not in the customerManagement domain folder to be sure
 * every global page change will also be applied to it.
 */
export const FunnelPage: FunctionComponent<
  React.PropsWithChildren<FunnelPageProps>
> = ({
  children,
  headerBackText,
  headerBackUrl,
  headerOnBackAction,
  pageName,
  pageType,
  pageTitle,
  pageCustomData,
  pageViewCustomData,
  statusCode,
  robotsContent = 'noindex, nofollow',
  ...metaTagsProps
}) => {
  usePageData({ pageName, pageType, pageCustomData, pageViewCustomData });

  /*TODO: this was a call to 'useFeatureFlags' replaced with the value of the flag/abtest. Consider removing this call when you modify the file.*/
  const [experimentalFFProgressBar] = [true];
  const isMobileApp = useSelector(isMobileAppUserAgentSelector);
  const isPaymentProcessing = useSelector(selectIsProcessingPayment);

  return (
    <>
      <MetaTags
        pageName={pageName}
        pageTitle={pageTitle}
        pageType={pageType}
        robotsContent={robotsContent}
        {...metaTagsProps}
      />
      <FlagsOverrideChecker />
      {experimentalFFProgressBar && <ProgressBar />}
      {!isMobileApp && (
        <FunnelHeader
          backText={headerBackText}
          backUrl={headerBackUrl}
          onBackAction={headerOnBackAction}
          disabled={isPaymentProcessing}
        />
      )}
      <BaseErrors statusCode={statusCode}>{children}</BaseErrors>
      {pageType === 'refund_form' && <Footer pageType={pageType} />}
    </>
  );
};
